// The lattice of rites, drawn as three tablets (sink, still, rise). Each
// node shows its own glyph, so the map is also a key to how the glyphs are
// built, for a player who looks. A tablet whose nine rites are won folds:
// its mark becomes the one card it folded into. When all three are whole,
// the Ternion waits under them: the last rite, which folds the three. Which
// rites are open is decided by src/domain/lattice.ts; this only draws and
// listens.

import { ICONS } from "../config/icons.ts"
import { RULES } from "../config/rules.ts"
import { WORDS } from "../config/words.ts"
import { html, unsafeRawHtml, type Html } from "../boundary/html.ts"
import { LAYERS, litLines, LINES, nodeState, solvedCount, tabletWhole, ternionState, type Layer, type Line } from "../domain/lattice.ts"
import { inPlay, keysOf, type Progress } from "../domain/progress.ts"
import { ALL_RITE_IDS, pointOfRite, riteIdOf, TERNION_ID, type RiteId, type WonKey } from "../domain/rite.ts"
import { digits, valueOf, type Trit } from "../domain/rings.ts"
import type { Clock } from "../effects/clock.ts"
import type { Sound } from "../effects/sound.ts"
import { closest, maybePart, replay, setHtml } from "./dom.ts"
import { indexRow, sigil } from "./glyph.ts"

export type LatticeDeps = {
  readonly sound: Sound
  readonly clock: Clock
  readonly exists: (id: RiteId) => boolean
  readonly par: (id: RiteId) => number | undefined
  readonly open: (id: RiteId) => void
  readonly ternion: () => void
}

export type LatticeView = {
  // `justWon` is the rite won a moment ago, if any: it rings and the finale may play.
  // `inPlace`: the map is already on screen (begin anew), so nothing on it may move.
  readonly render: (progress: Progress, justWon: WonKey | "none", inPlace: boolean) => void
}

const anewSvg = (cls: string, svg: string): Html => unsafeRawHtml(`<svg class="${cls}" viewBox="0 0 24 24" aria-hidden="true">${svg}</svg>`)

// Begin anew, a round button centred under the tablets once play has begun:
// the spiral, and once pressed the question mark (the page shows one or the
// other). The page (src/ui/app.ts) answers its press.
const anewHtml = (progress: Progress): Html =>
  keysOf(progress.seen).length === 0
    ? html``
    : html`<button type="button" class="anew-mark" data-act="anew" aria-label="${WORDS.anew.ask}" title="${WORDS.anew.ask}">${anewSvg("anew-glyph", ICONS.anew.svg)}${anewSvg("anew-ask", ICONS.ask.svg)}</button>`

export const numeral = (n: number, places: number): Html => indexRow(digits(Math.min(n, (3 ** places - 1) / 2), places))

const TRITS: readonly Trit[] = [-1, 0, 1]
const ROWS: readonly Trit[] = [1, 0, -1]

export const createLattice = (root: HTMLElement, progressBar: HTMLElement, deps: LatticeDeps): LatticeView => {
  const { sound } = deps

  // A rite that is locked, or already won, does not open: it shakes. Scott,
  // 2026-10-19: walking back into a won rite only confused.
  const shut = (el: HTMLElement): boolean => ["locked", "solved", "pure"].includes(el.dataset["state"] ?? "")
  const refuse = (el: HTMLElement): void => {
    sound.refuse()
    replay(el, "nope")
  }

  root.addEventListener("click", (e) => {
    const t = closest(e, ".ternion")
    if (t !== undefined && shut(t)) return refuse(t)
    if (t !== undefined) {
      sound.wake()
      sound.chime(0)
      deps.ternion()
      return
    }
    const n = closest(e, ".node")
    const id = ALL_RITE_IDS.find((r) => r === n?.dataset["id"])
    if (n === undefined || id === undefined) return
    if (shut(n)) return refuse(n)
    sound.wake()
    sound.chime(valueOf(pointOfRite(id)) / 2)
    deps.open(id)
  })

  // Shown on hover: the best number of moves so far, and par, in numerals.
  const tip = (progress: Progress, id: RiteId): Html => {
    const best = progress.solved[id]
    const par = deps.par(id)
    const row = (label: string, n: number): Html => html`<span class="tip-row"><i>${label}</i>${numeral(n, RULES.counterDigits)}</span>`
    return html`<span class="node-tip">${best === undefined ? "" : row("best", best.moves)}${par === undefined ? "" : row("par", par)}</span>`
  }

  const node = (progress: Progress, justWon: WonKey | "none") => (x: Trit, y: Trit, z: Trit): Html => {
    const id = riteIdOf(x, y, z)
    const st = nodeState(progress, deps.exists, id)
    const playing = progress.solved[id] === undefined && inPlay(progress, id) ? " playing" : ""
    const fresh = justWon === id ? " fresh" : ""
    return html`<button type="button" class="node${playing}${fresh}" data-id="${id}" data-state="${st}" style="--x:${x + 1};--y:${1 - y}" aria-label="Rite">
              <span class="node-ring"></span>${sigil([x, y, z])}${st === "locked" ? "" : tip(progress, id)}
            </button>`
  }

  const tablet = (progress: Progress, justWon: WonKey | "none", lit: readonly Line[], finale: boolean) => (layer: Layer): Html => {
    const z = layer.z
    const whole = tabletWhole(progress, z)
    const folding = whole && justWon !== "none" && justWon !== TERNION_ID && pointOfRite(justWon)[2] === z
    const draw = node(progress, justWon)
    const nodes = ROWS.flatMap((y) => TRITS.map((x) => draw(x, y, z)))
    const delay = (l: Line): string => (finale ? ` style="animation-delay:${LINES.indexOf(l) * 70}ms"` : "")
    // the lines lit inside this tablet (along rings 0 and 1)
    const segs = lit
      .filter((l) => l.axis !== 2 && pointOfRite(l.pts[0])[2] === z)
      .map((l) => {
        const a = pointOfRite(l.pts[0])
        const b = pointOfRite(l.pts[2])
        return unsafeRawHtml(`<line${delay(l)} x1="${a[0] + 1}" y1="${1 - a[1]}" x2="${b[0] + 1}" y2="${1 - b[1]}"/>`)
      })
    // pillars: lines through all three tablets, shown as a mark on each node
    const pillars = lit
      .filter((l) => l.axis === 2)
      .map((l) => {
        const a = pointOfRite(l.pts[0])
        return unsafeRawHtml(`<circle${delay(l)} cx="${a[0] + 1}" cy="${1 - a[1]}" r="0.36"/>`)
      })
    const grid = [0, 1, 2].map((k) => unsafeRawHtml(`<line x1="0" y1="${k}" x2="2" y2="${k}"/><line x1="${k}" y1="0" x2="${k}" y2="2"/>`))
    return html`<div class="tablet${whole ? " whole" : ""}${folding ? " folding" : ""}" data-layer="${layer.name}">
          <div class="tablet-mark">${sigil(whole ? ["folded", "folded", z] : [z], "layer-sigil")}</div>
          <div class="tablet-grid">
            <svg class="tablet-lines" viewBox="-0.5 -0.5 3 3" preserveAspectRatio="none" aria-hidden="true">
              <g class="grid">${grid}</g>
              <g class="lit">${segs}</g><g class="pillars">${pillars}</g>
            </svg>
            ${nodes}
          </div>
        </div>`
  }

  // The Ternion: once every tablet is whole, a ring under them opens the
  // last rite; won, it is the Monad, and a press plays it again.
  const ternionHtml = (progress: Progress): Html => {
    const st = ternionState(progress)
    if (st === "locked") return html``
    const won = st !== "open"
    return html`<button type="button" class="ternion${won ? " lattice-whole" : ""}" data-id="${TERNION_ID}" data-state="${st}" aria-label="${WORDS.ternion}">
        <span class="node-ring"></span>${sigil(["folded", "folded", "folded"])}
      </button>`
  }

  // Begin anew takes away the Ternion and its own button, under the tablets.
  // Their row keeps its height until the map is next shown, or the tablets
  // would drop (Scott, 2026-10-20: "everything shifts down ... it looks
  // glitchy"), or, on a phone, the page would scroll.
  const keepRoom = (height: number | undefined): void => {
    const under = maybePart(root, ".lattice-under")
    if (under !== undefined && height !== undefined) under.style.minHeight = `${height}px`
  }

  const render = (progress: Progress, justWon: WonKey | "none", inPlace: boolean): void => {
    const room = inPlace ? maybePart(root, ".lattice-under")?.getBoundingClientRect().height : undefined
    const solved = solvedCount(progress)
    const pure = ALL_RITE_IDS.filter((id) => progress.solved[id]?.pure === true).length
    setHtml(
      progressBar,
      html`<span class="mp-n">${numeral(solved, 4)}</span><span class="mp-of">of</span><span class="mp-n">${numeral(ALL_RITE_IDS.length, 4)}</span>${
        pure > 0 ? html`<span class="mp-pure">${numeral(pure, 4)}</span>` : html``
      }`,
    )
    const finale = justWon === TERNION_ID
    const lit = litLines(progress)
    setHtml(
      root,
      html`<div class="tablets">${LAYERS.map(tablet(progress, justWon, lit, finale))}</div><div class="lattice-under">${ternionHtml(progress)}${anewHtml(progress)}</div>`,
    )
    keepRoom(room)
    root.classList.toggle("finale", finale)
    if (finale) deps.clock.after(1800, () => sound.monad(3))
    if (justWon === "none") return
    // a tablet just made whole folds its three lines into one card
    const z = justWon === TERNION_ID ? undefined : pointOfRite(justWon)[2]
    if (z !== undefined && tabletWhole(progress, z)) deps.clock.after(900, () => sound.fold([9 * z + 3, 9 * z, 9 * z - 3], 2, 3))
    deps.clock.after(2400, () => root.querySelector(`[data-id="${justWon}"]`)?.classList.remove("fresh"))
    // rites that just opened ring softly
    deps.clock.after(500, () => sound.chime(4))
  }

  return { render }
}
